/**
 * Labels and display helpers for company job postings. The API owns status and
 * expiry (`status`, `is_expired`) — these helpers only format what it sends.
 */

import dayjs from 'dayjs'
import { parseServerDate } from '@/utils/dateHelper'

export const EMPLOYMENT_TYPES = [
  { value: 'full_time', text: 'Full time' },
  { value: 'part_time', text: 'Part time' },
  { value: 'casual', text: 'Casual' },
  { value: 'contract', text: 'Contract' },
  { value: 'apprentice', text: 'Apprentice' },
  { value: 'labour_hire', text: 'Labour hire' },
]

export const PAY_TYPES = [
  { value: 'hourly', text: 'Hourly' },
  { value: 'salary', text: 'Salary' },
  { value: 'daily', text: 'Daily' },
  { value: 'negotiable', text: 'Negotiable' },
]

// Who a job is advertised to. Mirrors JobPosting::VISIBILITIES in the API.
export const VISIBILITY_OPTIONS = [
  { value: 'external', text: 'External - tradies outside my workforce' },
  { value: 'internal', text: 'Internal - my workforce only' },
  { value: 'both', text: 'Both - internal and external' },
]

export const AU_STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT']

// Pulls suburb / state / postcode out of a free-text company address such as
// "12 Smith St, Fremantle WA 6160, Australia". Suburb and state are required;
// the postcode is optional because address autocomplete often omits it
// ("123 Wallaby Road, Tabulam NSW, Australia"). Returns null without a
// suburb + state, so a half-parsed guess is never pre-filled.
export function parseAddressLocation(address) {
  if (!address) return null
  const pattern = new RegExp(
    `([A-Za-z][A-Za-z' .-]*?),?\\s+(${AU_STATES.join(
      '|',
    )})\\b(?:\\s+(\\d{4})\\b)?`,
  )
  const match = String(address).match(pattern)
  if (!match) return null
  // Drop any street part left in front of the suburb ("12 Smith St, Suburb").
  const suburb = match[1].split(',').pop().trim()
  if (!suburb) return null
  return { suburb, state: match[2], postcode: match[3] || '' }
}

// Options for the list filter, in the same order the list is sorted
// (published, expired, closed, draft). `expired` is a server-side filter value.
export const STATUS_FILTERS = [
  { value: '', text: 'All statuses' },
  { value: 'published', text: 'Published' },
  { value: 'scheduled', text: 'Scheduled' },
  { value: 'expired', text: 'Expired' },
  { value: 'closed', text: 'Closed' },
  { value: 'draft', text: 'Draft' },
]

const STATUS_LABELS = {
  draft: 'Draft',
  published: 'Published',
  closed: 'Closed',
  removed: 'Removed',
  expired: 'Expired',
  scheduled: 'Scheduled',
}

const STATUS_VARIANTS = {
  draft: 'secondary',
  published: 'success',
  closed: 'dark',
  removed: 'danger',
  expired: 'warning',
  scheduled: 'info',
}

export const APPLICATION_STATUS_LABELS = {
  new: 'New',
  shortlisted: 'Shortlisted',
  interview: 'Interview',
  offer: 'Offer',
  hired: 'Hired',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
}

// The same application states, worded for the tradie who applied. A rejection
// reads "Not successful" rather than the company's internal "Rejected", and
// the company's Interview / Offer stages read as Shortlisted to the tradie.
export const TRADIE_APPLICATION_STATUS_LABELS = {
  new: 'Applied',
  shortlisted: 'Shortlisted',
  interview: 'Shortlisted',
  offer: 'Shortlisted',
  hired: 'Hired',
  rejected: 'Not successful',
  withdrawn: 'Withdrawn',
  // Display only: the API reads a job that has closed or lapsed as closed.
  closed: 'Closed',
}

// The tradie's stage on a job-board row or open job; 'new' until the API says more.
export function myApplicationStatus(job) {
  if (!job || !job.has_applied) return null
  return job.application_status || 'new'
}

export function tradieStatusText(status) {
  return TRADIE_APPLICATION_STATUS_LABELS[status] || status
}

export function tradieStatusVariant(status) {
  return status === 'rejected' ? 'secondary' : 'success'
}

// A rejected or hired application has nothing left to withdraw.
export function canWithdrawStatus(status) {
  return ['new', 'shortlisted', 'interview', 'offer'].includes(status)
}

// Stages a company can move an applicant to once they have shared their
// contact details, in order.
export const CONTACT_STAGES = ['interview', 'offer', 'hired']

export function employmentTypeLabel(value) {
  const hit = EMPLOYMENT_TYPES.find((t) => t.value === value)
  return hit ? hit.text : value || '-'
}

/**
 * Display status for a job: the API's `is_expired` flag (never computed here)
 * is shown in place of `published` so a lapsed live job reads as Expired, and
 * `is_scheduled` so a job waiting for its go-live date reads as Scheduled.
 */
export function displayStatus(job) {
  if (!job) return ''
  if (job.is_expired && job.status === 'published') return 'expired'
  if (job.is_scheduled && job.status === 'published') return 'scheduled'
  return job.status
}

export function statusLabel(job) {
  return STATUS_LABELS[displayStatus(job)] || displayStatus(job)
}

export function statusVariant(job) {
  return STATUS_VARIANTS[displayStatus(job)] || 'secondary'
}

/** "Perth WA 6000" style summary, skipping any missing parts. */
export function locationLabel(job) {
  const loc = (job && job.location) || {}
  return [loc.suburb, loc.state, loc.postcode].filter(Boolean).join(' ') || '-'
}

function money(value) {
  return '$' + Number(value).toLocaleString('en-AU')
}

/** Pay summary honouring the company's show_pay choice. */
export function payLabel(job) {
  const pay = job && job.pay
  if (!pay || !pay.type) return '-'
  if (pay.type === 'negotiable') return 'Negotiable'
  const unit = { hourly: '/hr', daily: '/day', salary: '/yr' }[pay.type] || ''
  const hasMin = pay.min !== null && pay.min !== undefined && pay.min !== ''
  const hasMax = pay.max !== null && pay.max !== undefined && pay.max !== ''
  if (!hasMin && !hasMax) return '-'
  if (hasMin && hasMax && Number(pay.min) !== Number(pay.max)) {
    return `${money(pay.min)} - ${money(pay.max)}${unit}`
  }
  return `${money(hasMin ? pay.min : pay.max)}${unit}`
}

/** dd/mm/yyyy from an API timestamp, or '-'. Display only. */
export function formatDate(value) {
  const d = parseServerDate(value)
  if (!d) return '-'
  return d.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

/** "Today", "Yesterday", "3 days ago", then a plain date after a month. Display only. */
export function timeAgo(value) {
  const d = parseServerDate(value)
  if (!d) return '-'
  const days = Math.floor((Date.now() - d.getTime()) / 86400000)
  if (days <= 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 30) return `${days} days ago`
  return formatDate(value)
}

/** Today as YYYY-MM-DD (local) — the datepicker `min` for expiry. */
export function todayISO() {
  return dayjs().format('YYYY-MM-DD')
}

// A job can be live for at most this many days from publishing. Mirrors
// JobPosting::MAX_LIVE_DAYS in the API.
export const MAX_LIVE_DAYS = 30

// The latest expiry date the datepicker offers (YYYY-MM-DD). The API counts the
// 30 days from the current UTC date, so this does too: counting from the
// browser's local date would offer one day too many to someone in Perth before
// 8am, and the API would then refuse it.
export function maxExpiryISO(now = new Date()) {
  const d = new Date(now.getTime())
  d.setUTCDate(d.getUTCDate() + MAX_LIVE_DAYS)
  return d.toISOString().slice(0, 10)
}

// A job can be scheduled to go live at most this many days ahead. Mirrors
// JobPosting::MAX_SCHEDULE_DAYS in the API, so the most a post can reach is
// MAX_SCHEDULE_DAYS + MAX_LIVE_DAYS from today.
export const MAX_SCHEDULE_DAYS = 30

// A YYYY-MM-DD date plus n days (UTC arithmetic, so daylight saving never moves it).
export function addDaysISO(iso, days) {
  const d = new Date(`${iso}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

// Today's UTC date: the API counts its 30 day limits from this, not the browser's.
export function utcTodayISO(now = new Date()) {
  return now.toISOString().slice(0, 10)
}

// The latest go-live date the picker offers.
export function maxLiveFromISO(now = new Date()) {
  return addDaysISO(utcTodayISO(now), MAX_SCHEDULE_DAYS)
}

// The latest expiry the picker offers when no go-live date has been chosen:
// scheduled the furthest ahead and then live for the full window.
export function maxReachISO(now = new Date()) {
  return addDaysISO(utcTodayISO(now), MAX_SCHEDULE_DAYS + MAX_LIVE_DAYS)
}

// The latest expiry for a go-live date ('' = live from now). A go-live of today
// or earlier means live now, so the window runs from the current UTC date.
export function maxExpiryFor(liveFrom, today = todayISO(), now = new Date()) {
  const start = liveFrom && liveFrom > today ? liveFrom : utcTodayISO(now)
  return addDaysISO(start, MAX_LIVE_DAYS)
}

// The user picked a go-live date: the expiry follows. A blank expiry (or one
// outside the new window) becomes the full 30 days; a shorter one they chose
// stays.
export function expiryAfterLiveFromChange(
  { liveFrom, expiresAt },
  today = todayISO(),
  now = new Date(),
) {
  if (!liveFrom) return expiresAt
  const max = maxExpiryFor(liveFrom, today, now)
  const beforeGoLive = liveFrom > today && expiresAt && expiresAt <= liveFrom
  return !expiresAt || expiresAt > max || beforeGoLive ? max : expiresAt
}

// The user picked an expiry: the go-live date follows. It sits 30 days before
// the expiry but never before today. A go-live that already makes a valid
// window stays.
export function liveFromAfterExpiryChange(
  { liveFrom, expiresAt },
  today = todayISO(),
  now = new Date(),
) {
  if (!expiresAt) return liveFrom
  const earliest = addDaysISO(expiresAt, -MAX_LIVE_DAYS)
  const fallback = earliest > today ? earliest : today
  if (!liveFrom) return fallback
  const tooLate = expiresAt > maxExpiryFor(liveFrom, today, now)
  const beforeGoLive = liveFrom > today && expiresAt <= liveFrom
  return tooLate || beforeGoLive ? fallback : liveFrom
}

// Roster on a job ad. Mirrors JobPosting::ROSTERS in the API.
export const ROSTER_OPTIONS = [
  { value: '2:1', text: '2:1' },
  { value: '8:6', text: '8:6' },
  { value: '7:7', text: '7:7' },
  { value: 'flexible', text: 'Flexible' },
]

// Mirrors JobPosting::EXPERIENCE_LEVELS in the API.
export const EXPERIENCE_LEVELS = [
  { value: 'entry', text: 'Entry level' },
  { value: 'intermediate', text: 'Intermediate' },
  { value: 'experienced', text: 'Experienced' },
  { value: 'senior', text: 'Senior' },
]

export function rosterLabel(value) {
  const hit = ROSTER_OPTIONS.find((o) => o.value === value)
  return hit ? hit.text : value || '-'
}

export function experienceLevelLabel(value) {
  const hit = EXPERIENCE_LEVELS.find((o) => o.value === value)
  return hit ? hit.text : value || '-'
}

/** "Accommodation, Travel" from the two provided flags, or '-'. */
export function providedLabel(job) {
  const parts = []
  if (job && job.accommodation_provided) parts.push('Accommodation')
  if (job && job.travel_provided) parts.push('Travel')
  return parts.join(', ') || '-'
}
